import { Add } from '@mui/icons-material'
import {
  Alert,
  AlertProps,
  Box,
  Button,
  MenuItem,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { DataGrid, GridColDef, GridRenderCellParams, GridRowModel } from '@mui/x-data-grid'
import { doc, setDoc } from 'firebase/firestore'
import { useMemo, useState } from 'react'

import {
  DEFAULT_WORKOUT_ASSET,
  DEFAULT_WORKOUT_COLOR,
  useWorkoutSettings,
  WORKOUT_ASSETS,
  WORKOUT_COLORS,
  WorkoutType,
} from '../../../providers/workout-settings'
import { firestore } from '../../firebaseInit'
import TableToolbar from './table-toolbar'

function slugify(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[åä]/g, 'a')
    .replace(/ö/g, 'o')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

function workoutTypeDoc(id: string) {
  return doc(firestore, 'workoutTypes', id)
}

function assetLabel(asset: string) {
  return asset
    .replace('/assets/', '')
    .replace('.png', '')
    .replace(/-/g, ' ')
}

function allowedColor(color?: string) {
  return WORKOUT_COLORS.includes(color ?? '') ? color : DEFAULT_WORKOUT_COLOR
}

export default function WorkoutTypesTable() {
  const { workoutTypes, loading, error } = useWorkoutSettings()
  const [name, setName] = useState('')
  const [assetPath, setAssetPath] = useState(DEFAULT_WORKOUT_ASSET)
  const [color, setColor] = useState(DEFAULT_WORKOUT_COLOR)
  const [snackbar, setSnackbar] = useState<Pick<
    AlertProps,
    'children' | 'severity'
  > | null>(null)

  const rows = useMemo(
    () =>
      workoutTypes.map((workoutType) => ({
        ...workoutType,
        active: workoutType.active ?? true,
        bookable: workoutType.bookable ?? true,
        image: workoutType.image ?? DEFAULT_WORKOUT_ASSET,
        color: allowedColor(workoutType.color),
      })),
    [workoutTypes]
  )

  const handleCreate = async () => {
    const trimmedName = name.trim()
    const id = slugify(trimmedName)

    if (!trimmedName || !id) return

    try {
      await setDoc(workoutTypeDoc(id), {
        name: trimmedName,
        active: true,
        bookable: true,
        image: assetPath || DEFAULT_WORKOUT_ASSET,
        color: color || DEFAULT_WORKOUT_COLOR,
        sortOrder: workoutTypes.length * 10 + 10,
      })
      setName('')
      setAssetPath(DEFAULT_WORKOUT_ASSET)
      setColor(DEFAULT_WORKOUT_COLOR)
      setSnackbar({ children: 'Passtypen lades till', severity: 'success' })
    } catch (error) {
      console.log(error)
      setSnackbar({
        children: 'Passtypen kunde inte sparas',
        severity: 'error',
      })
    }
  }

  const processRowUpdate = async (newRow: GridRowModel) => {
    const workoutType = newRow as WorkoutType

    await setDoc(
      workoutTypeDoc(workoutType.id),
      {
        name: workoutType.name,
        active: workoutType.active ?? true,
        bookable: workoutType.bookable ?? true,
        color: allowedColor(workoutType.color),
        image: WORKOUT_ASSETS.includes(workoutType.image ?? '')
          ? workoutType.image
          : DEFAULT_WORKOUT_ASSET,
        sortOrder: Number(workoutType.sortOrder ?? 0),
      },
      { merge: true }
    )

    setSnackbar({ children: 'Passtypen uppdaterades', severity: 'success' })
    return newRow
  }

  const handleBooleanChange = async (
    id: string,
    field: 'active' | 'bookable',
    value: boolean
  ) => {
    try {
      await setDoc(workoutTypeDoc(id), { [field]: value }, { merge: true })
      setSnackbar({
        children:
          field === 'bookable'
            ? value
              ? 'Passtypen är bokningsbar'
              : 'Passtypen är inte bokningsbar'
            : value
            ? 'Passtypen aktiverades'
            : 'Passtypen inaktiverades',
        severity: 'success',
      })
    } catch (error) {
      console.log(error)
      setSnackbar({
        children: 'Passtypen kunde inte uppdateras',
        severity: 'error',
      })
    }
  }

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', minWidth: 160 },
    { field: 'name', headerName: 'Namn', editable: true, minWidth: 220, flex: 1 },
    {
      field: 'bookable',
      headerName: 'Bokningsbar',
      minWidth: 130,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip
          title={params.value ? 'Gör ej bokningsbar' : 'Gör bokningsbar'}
        >
          <Switch
            size="small"
            checked={Boolean(params.value)}
            onChange={(event) =>
              handleBooleanChange(params.row.id, 'bookable', event.target.checked)
            }
          />
        </Tooltip>
      ),
    },
    {
      field: 'active',
      headerName: 'Aktiv',
      minWidth: 100,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={params.value ? 'Inaktivera passtyp' : 'Aktivera passtyp'}>
          <Switch
            size="small"
            checked={Boolean(params.value)}
            onChange={(event) =>
              handleBooleanChange(params.row.id, 'active', event.target.checked)
            }
          />
        </Tooltip>
      ),
    },
    {
      field: 'image',
      headerName: 'Bild',
      type: 'singleSelect',
      valueOptions: WORKOUT_ASSETS,
      editable: true,
      minWidth: 220,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const image = params.value || DEFAULT_WORKOUT_ASSET

        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              component="img"
              src={image}
              alt=""
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                objectFit: 'cover',
              }}
            />
            <Typography variant="body2">{assetLabel(image)}</Typography>
          </Stack>
        )
      },
    },
    {
      field: 'color',
      headerName: 'Färg',
      type: 'singleSelect',
      valueOptions: WORKOUT_COLORS,
      editable: true,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => {
        const selectedColor = allowedColor(params.value)

        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                backgroundColor: selectedColor,
                border: '1px solid',
                borderColor: 'divider',
              }}
            />
            <Typography variant="body2">{selectedColor}</Typography>
          </Stack>
        )
      },
    },
    {
      field: 'sortOrder',
      headerName: 'Sortering',
      type: 'number',
      editable: true,
      minWidth: 120,
    },
  ]

  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ py: 2 }}>
        <TextField
          label="Ny passtyp"
          value={name}
          onChange={(event) => setName(event.target.value)}
          onKeyUp={(event) => event.key === 'Enter' && handleCreate()}
          fullWidth
        />
        <TextField
          select
          label="Bild"
          value={assetPath}
          onChange={(event) => setAssetPath(event.target.value)}
          sx={{ minWidth: { xs: '100%', sm: 220 } }}
        >
          {WORKOUT_ASSETS.map((asset) => (
            <MenuItem key={asset} value={asset}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box
                  component="img"
                  src={asset}
                  alt=""
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 1,
                    objectFit: 'cover',
                  }}
                />
                <span>{assetLabel(asset)}</span>
              </Stack>
            </MenuItem>
          ))}
        </TextField>
        <Box sx={{ minWidth: { xs: '100%', sm: 260 } }}>
          <Typography variant="caption" color="text.secondary">
            Färg
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={color}
            onChange={(_, value) => value && setColor(value)}
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
              mt: 0.25,
              '& .MuiToggleButtonGroup-grouped': {
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                m: 0,
              },
            }}
          >
            {WORKOUT_COLORS.map((workoutColor) => (
              <ToggleButton
                key={workoutColor}
                value={workoutColor}
                aria-label={`Välj färg ${workoutColor}`}
                sx={{
                  width: 32,
                  height: 32,
                  p: 0,
                }}
              >
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    backgroundColor: workoutColor,
                  }}
                />
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          disabled={!name.trim()}
          onClick={handleCreate}
          sx={{ minWidth: 140 }}
        >
          Lägg till
        </Button>
      </Stack>
      {!error && (
        <DataGrid
          autoHeight
          experimentalFeatures={{ newEditingApi: true }}
          components={{ Toolbar: TableToolbar }}
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          processRowUpdate={processRowUpdate}
          pageSize={10}
          rowsPerPageOptions={[10]}
          hideFooterSelectedRowCount
          disableSelectionOnClick
        />
      )}
      {error && <DataGrid error={error} columns={[]} rows={[]} />}
      {!!snackbar && (
        <Snackbar
          open
          autoHideDuration={5000}
          onClose={() => setSnackbar(null)}
        >
          <Alert {...snackbar} onClose={() => setSnackbar(null)} />
        </Snackbar>
      )}
    </Box>
  )
}
